import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";

import "./scratch-card-confirm-modal.css";

export interface ScratchCardConfirmModalProps {
  /** Tên nhà mạng khách đã chọn (dùng cho alt/fallback khi thiếu logo). */
  carrierName: string;
  /** Logo nhà mạng đã chọn (ảnh sẵn có); thiếu thì hiện tên. */
  carrierLogo?: string;
  /** Mệnh giá thẻ (VND, số) — hiển thị dạng "20.000đ". */
  rewardAmount: number;
  /** Đang gọi API phát thưởng — khoá nút + hiện spinner trên "Nhận thẻ ngay". */
  loading?: boolean;
  /** Xác nhận nhận thẻ (gọi API phát thưởng). */
  onConfirm: () => void;
  /** Quay lại chọn nhà mạng (nút "Chọn lại"). */
  onReselect: () => void;
  /** Đóng modal (✕ / Esc / click overlay). */
  onClose: () => void;
}

const formatVnd = (value: number): string =>
  new Intl.NumberFormat("vi-VN").format(Math.max(0, value));

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

const Spinner: React.FC = () => (
  <span className="gp-cm__spinner" aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  </span>
);

/**
 * Popup xác nhận nhà mạng trước khi phát thẻ cào (redesign hệ đỏ ấm).
 * Custom dialog (không dùng antd Modal) để kiểm soát overlay/focus-trap/scroll-lock:
 *  - đóng khi ✕ / Esc / click overlay / "Chọn lại"
 *  - khoá scroll nền, trap Tab trong modal, focus mặc định nút "Nhận thẻ ngay"
 *  - accessible: role="dialog" + aria-modal + aria-labelledby
 */
const ScratchCardConfirmModal: React.FC<ScratchCardConfirmModalProps> = ({
  carrierName,
  carrierLogo,
  rewardAmount,
  loading = false,
  onConfirm,
  onReselect,
  onClose,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  // Khoá scroll nền khi mở; khôi phục khi đóng.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Focus mặc định vào "Nhận thẻ ngay"; trả focus về phần tử trước đó khi đóng.
  useEffect(() => {
    const prevActive = document.activeElement as HTMLElement | null;
    confirmRef.current?.focus();
    return () => prevActive?.focus?.();
  }, []);

  // Esc để đóng + trap focus trong modal (Tab vòng lại trong dialog).
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      if (!loading) onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const nodes = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && (active === first || !dialog.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const onOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Chỉ đóng khi bấm thẳng vào overlay (không phải khi thao tác trong card).
    if (e.target === e.currentTarget && !loading) onClose();
  };

  return createPortal(
    <div className="gp-cm__overlay" onMouseDown={onOverlayMouseDown}>
      <div
        ref={dialogRef}
        className="gp-cm"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
      >
        <div className="gp-cm__head">
          <h2 id={titleId} className="gp-cm__title">
            Xác nhận nhận thẻ
          </h2>
          <button
            type="button"
            className="gp-cm__close"
            aria-label="Đóng"
            disabled={loading}
            onClick={onClose}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="gp-cm__ticket">
          <span className="gp-cm__ticket-dot gp-cm__ticket-dot--l" />
          <span className="gp-cm__ticket-dot gp-cm__ticket-dot--r" />
          <div className="gp-cm__ribbon">🎁 THẺ CÀO ĐIỆN THOẠI</div>
          <div className="gp-cm__amount">
            {formatVnd(rewardAmount)}
            <span className="gp-cm__unit">đ</span>
          </div>
          <div className="gp-cm__net">
            {carrierLogo ? (
              <img className="gp-cm__net-logo" src={carrierLogo} alt={carrierName} />
            ) : (
              <span className="gp-cm__net-name">{carrierName}</span>
            )}
            <span className="gp-cm__net-badge">Nhà mạng đã chọn</span>
          </div>
        </div>

        <p className="gp-cm__desc">
          Kiểm tra đúng nhà mạng đang sử dụng rồi bấm xác nhận để nhận thẻ.
        </p>

        <div className="gp-cm__actions">
          <button
            type="button"
            className="gp-cm__btn gp-cm__btn--secondary"
            disabled={loading}
            onClick={onReselect}
          >
            Chọn lại
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="gp-cm__btn gp-cm__btn--primary"
            disabled={loading}
            onClick={onConfirm}
          >
            {loading ? (
              <>
                <Spinner />
                Đang xử lý…
              </>
            ) : (
              "Nhận thẻ ngay"
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ScratchCardConfirmModal;
